import React, { useState, useRef, useEffect } from "react";

interface EditableProps {
  text: string;
  onEdit: (value: string) => void;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}

const EditableDiv: React.FC<EditableProps> = ({
  text,
  onEdit,
  className = "",
  style = {},
  placeholder = "Click to edit",
}) => {
  const [value, setValue] = useState(text);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const newValue = e.currentTarget.innerText;
    setValue(newValue);
    onEdit(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      divRef.current?.blur();
    }
  };

  return (
    <div
      ref={divRef}
      contentEditable
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={`cursor-text outline-none ${className}`}
      style={{
        ...style,
        transition: "all 0.2s ease",
      }}
      suppressContentEditableWarning
    >
      {value || placeholder}
    </div>
  );
};

interface SlideContent {
  title?: string;
  subtitle?: string;
  topic?: string;
  bgColor?: string;
  onEdit?: (content: SlideContent) => void;
}

const HeaderTitleSlide3 = {
  id: "header-title3-slide",
  name: "Header Title Slide (Particles)",
  fields: [
    {
      id: "title",
      label: "Title",
      type: "text",
    },
    {
      id: "subtitle",
      label: "Subtitle",
      type: "text",
    },
    {
      id: "topic",
      label: "Topic",
      type: "text",
    },
    {
      id: "bgColor",
      label: "Background Color",
      type: "text",
    },
  ],
  render: (content: SlideContent) => {
    const {
      title = "",
      subtitle = "",
      topic = "",
      bgColor,
      onEdit = () => {},
    } = content;

    const handleEdit = (field: keyof SlideContent) => (value: string) => {
      const updatedContent = { ...content, [field]: value };
      onEdit(updatedContent);

      // Update the configuration in PreviewSlides
      if (window.updateLiveConfig) {
        window.updateLiveConfig(updatedContent);
      }
    };

    return (
      <div
        className="w-full h-full relative overflow-hidden text-white"
        style={{ background: bgColor }}
      >
        {/* Particle Effect */}
        <div className="absolute inset-0">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="particle absolute rounded-full"
              style={{
                width: Math.random() * 4 + 1 + "px",
                height: Math.random() * 4 + 1 + "px",
                background: `rgba(255, 255, 255, ${Math.random() * 0.5 + 0.25})`,
                left: Math.random() * 100 + "%",
                top: Math.random() * 100 + "%",
                animation: `particle-${i} ${Math.random() * 20 + 10}s linear infinite`,
                boxShadow: "0 0 10px currentColor",
              }}
            />
          ))}
        </div>

        {/* Content Container */}
        <div className="relative z-10 flex flex-col h-full">
          {/* Top Bar */}
          <div className="p-8">
            <div className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <EditableDiv
                text={topic}
                onEdit={handleEdit("topic")}
                className="text-sm font-medium"
                placeholder="Enter topic..."
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-grow flex items-center justify-center p-12">
            <div className="max-w-5xl w-full space-y-12 text-center">
              {/* Title */}
              <div className="relative">
                <EditableDiv
                  text={title}
                  onEdit={handleEdit("title")}
                  className="text-7xl font-black tracking-tight leading-none animate-glow"
                  placeholder="Enter title..."
                />
                <div className="absolute -inset-4 bg-white/5 blur-3xl rounded-full -z-10"></div>
              </div>

              {/* Animated Separator */}
              <div className="flex justify-center items-center space-x-4">
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
                <div className="w-3 h-3 rounded-full bg-white/30 animate-ping"></div>
                <div className="w-12 h-px bg-gradient-to-r from-transparent via-white/50 to-transparent"></div>
              </div>

              {/* Subtitle */}
              <EditableDiv
                text={subtitle}
                onEdit={handleEdit("subtitle")}
                className="text-3xl font-light text-white/90 max-w-3xl mx-auto animate-fade-in"
                placeholder="Enter subtitle..."
              />
            </div>
          </div>

          {/* Bottom Decoration */}
          <div className="p-8 flex justify-center">
            <div className="flex space-x-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="w-8 h-1 rounded-full bg-white/30"
                  style={{
                    animation: `pulse 2s ${i * 0.3}s infinite`,
                  }}
                ></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  },
};

// Add required styles to the global CSS
const style = document.createElement("style");
style.textContent = `
  @keyframes glow {
    0%, 100% { text-shadow: 0 0 20px rgba(255, 255, 255, 0.5); }
    50% { text-shadow: 0 0 30px rgba(255, 255, 255, 0.8); }
  }

  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  ${[...Array(50)]
    .map(
      (_, i) => `
    @keyframes particle-${i} {
      0% {
        transform: translate(0, 0) rotate(0deg);
      }
      33% {
        transform: translate(${Math.random() * 200 - 100}px, ${Math.random() * 200 - 100}px) rotate(120deg);
      }
      66% {
        transform: translate(${Math.random() * 200 - 100}px, ${Math.random() * 200 - 100}px) rotate(240deg);
      }
      100% {
        transform: translate(0, 0) rotate(360deg);
      }
    }
  `,
    )
    .join("\n")}

  .animate-glow {
    animation: glow 3s ease-in-out infinite;
  }

  .animate-fade-in {
    animation: fade-in 0.8s ease-out forwards;
  }
`;
document.head.appendChild(style);

// Declare global window interface
declare global {
  interface Window {
    updateLiveConfig?: (config: SlideContent) => void;
  }
}

export default HeaderTitleSlide3;
